import { Component, Input, Output, EventEmitter, TemplateRef, ContentChild, OnChanges } from '@angular/core';
import { MatIconModule } from "@angular/material/icon";
import { CurrencyInputDirective } from '../directives/currency-input.directive';
import { NgClass, NgStyle, NgTemplateOutlet } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ColumnFilterComponent } from '../column-filter/column-filter.component';

type TextAlignment = 'left' | 'center' | 'right';

export interface TableColumn {
  key: string;
  header: string;
  headerSub?: string;
  width?: string;
  align?: TextAlignment; //to align entire column (th + td)
  headerAlign?: TextAlignment;
  contentAlign?: TextAlignment;
  editable?: boolean;
  filterable?: boolean;
  isCellEditableFn?: (row: any, rowIndex: number) => boolean;
  type?: 'text' | 'number' | 'currency' | 'percentage' | 'boolean';
  step?: string; // step attribute for number inputs (e.g. '0.01' for decimals); defaults to '1'
  maxDecimals?: number; // for number inputs: excess decimal digits are trimmed as the user types

  formatter?: (value: any, row?: any, rowIndex?: number) => string;
  tooltip?: (value: any, row?: any) => string;
  editFormatter?: (value: any) => any;
  cellClass?: (value: any, row?: any, rowIndex?: number) => string | string[] | null;
  headerClass?: string | string[];
}

export interface RowClassConfig {
  condition: (row: any, index: number) => boolean;
  className: string;
}

export interface GroupedHeaderCell {
  label: string;
  colspan: number;
  align?: TextAlignment;
  className?: string;
}

@Component({
  selector: 'app-data-table',
  templateUrl: './data-table.component.html',
  styleUrls: ['./data-table.component.less'],
  imports: [ MatIconModule, CurrencyInputDirective, NgClass, NgStyle, NgTemplateOutlet, FormsModule, ColumnFilterComponent],
  standalone: true
})
export class DataTableComponent implements OnChanges {
  @Input() columns: TableColumn[] = [];
  @Input() groupedHeaders: GroupedHeaderCell[] = [];
  @Input() data: any[] = [];
  @Input() loading = false;
  @Input() emptyMessage = 'No records found';
  @Input() loadingMessage = 'Loading...';
  @Input() transparent = false;
  
  @Input() editMode = false;
  @Input() editableFromIndex = 0;
  @Input() isRowEditable?: (row: any, rowIndex: number) => boolean;
  
  @Input() rowClasses: RowClassConfig[] = [];
  @Input() alternateRowColors = false;
  @Input() highlightOnHover = true;
  
  @Input() hasTotalRow = false;
  @Input() totalRowIndex = 0;

  @Input() showColumnFilters = false;

  displayRows: any[] = [];
  columnFilters = new Map<string, ReadonlySet<string>>();
  private displayRowIndices: number[] | null = null;
  private filterOptions = new Map<string, string[]>();
  private readonly noFilterOptions: string[] = [];

  @ContentChild('cellTemplate') cellTemplate?: TemplateRef<any>;
  @ContentChild('headerTemplate') headerTemplate?: TemplateRef<any>;
  
  @Input() trackByKey: string = 'id';
  
  @Output() cellValueChange = new EventEmitter<{ row: any; column: TableColumn; value: any; rowIndex: number }>();
  @Output() rowClick = new EventEmitter<{ row: any; rowIndex: number }>();
  @Output() historyClick = new EventEmitter<any>();

  ngOnChanges(): void {
    this.refreshRows();
  }

  get hasActiveFilters(): boolean {
    return this.columnFilters.size > 0;
  }

  isColumnFilterVisible(column: TableColumn): boolean {
    return this.showColumnFilters && !!column.filterable;
  }

  getFilterOptions(column: TableColumn): string[] {
    return this.filterOptions.get(column.key) ?? this.noFilterOptions;
  }

  onColumnFilterApplied(column: TableColumn, checkedOptions: ReadonlySet<string>): void {
    const shownOptions = new Set(this.getFilterOptions(column));
    const previous = this.columnFilters.get(column.key);
    const columnValues = this.collectColumnValues(column.key, false);

    const allowed = new Set<string>();
    for (const value of columnValues) {
      const isAllowed = shownOptions.has(value) ? checkedOptions.has(value) : previous?.has(value) ?? true;
      if (isAllowed) {
        allowed.add(value);
      }
    }
    previous?.forEach(value => {
      if (!columnValues.has(value)) {
        allowed.add(value);
      }
    });

    if ([...columnValues].every(value => allowed.has(value))) {
      this.columnFilters.delete(column.key);
    } else {
      this.columnFilters.set(column.key, allowed);
    }
    this.refreshRows();
  }

  onColumnFilterCleared(column: TableColumn): void {
    this.columnFilters.delete(column.key);
    this.refreshRows();
  }

  dataIndexOf(displayIndex: number): number {
    return this.displayRowIndices ? this.displayRowIndices[displayIndex] : displayIndex;
  }

  private refreshRows(): void {
    const filterableColumns = this.columns.filter(column => this.isColumnFilterVisible(column));
    this.pruneColumnFilters(filterableColumns);
    this.filterOptions = new Map(filterableColumns.map(column => [column.key, this.collectFilterOptions(column.key)]));

    if (!this.hasActiveFilters) {
      this.displayRows = this.data;
      this.displayRowIndices = null;
      return;
    }

    const indices: number[] = [];
    this.data.forEach((row, index) => {
      if (this.isPinnedRow(index) || this.passesFilters(row)) {
        indices.push(index);
      }
    });
    this.displayRowIndices = indices;
    this.displayRows = indices.map(index => this.data[index]);
  }

  private pruneColumnFilters(filterableColumns: TableColumn[]): void {
    const filterableKeys = new Set(filterableColumns.map(column => column.key));
    for (const key of this.columnFilters.keys()) {
      if (!filterableKeys.has(key)) {
        this.columnFilters.delete(key);
      }
    }
  }

  private collectFilterOptions(key: string): string[] {
    return [...this.collectColumnValues(key, true)]
      .sort((a, b) => a.localeCompare(b, undefined, { numeric: true, sensitivity: 'base' }));
  }

  private collectColumnValues(key: string, respectOtherFilters: boolean): Set<string> {
    const values = new Set<string>();
    this.data.forEach((row, index) => {
      if (!this.isPinnedRow(index) && (!respectOtherFilters || this.passesFilters(row, key))) {
        values.add(this.getFilterValue(row, key));
      }
    });
    return values;
  }

  private passesFilters(row: any, skipKey?: string): boolean {
    for (const [key, allowed] of this.columnFilters) {
      if (key !== skipKey && !allowed.has(this.getFilterValue(row, key))) {
        return false;
      }
    }
    return true;
  }

  private getFilterValue(row: any, key: string): string {
    const value = row?.[key];
    return value == null ? '' : String(value).trim();
  }

  private isPinnedRow(index: number): boolean {
    return this.hasTotalRow && index === this.totalRowIndex;
  }

  getCellValue(row: any, column: TableColumn, rowIndex: number): string {
    const value = row[column.key];
    
    if (column.formatter) {
      return column.formatter(value, row, rowIndex);
    }
    
    if (column.type === 'currency' && value != null) {
      return this.formatCurrency(value);
    }
    
    if (column.type === 'percentage' && value != null) {
      return `${value}%`;
    }
    
    if (column.type === 'boolean') {
      return value ? 'Yes' : 'No';
    }
    
    return value ?? '-';
  }

  // While a cell input is focused it owns its text: the [value] binding keeps returning the raw
  // typed text so Angular never rewrites the input mid-edit (which would reset the caret).
  private editingCell: { row: any; key: string; text: string } | null = null;

  private isEditingCell(row: any, column: TableColumn): boolean {
    return this.editingCell != null && this.editingCell.row === row && this.editingCell.key === column.key;
  }

  onEditFocus(row: any, column: TableColumn, event: Event): void {
    this.editingCell = { row, key: column.key, text: (event.target as HTMLInputElement).value };
  }

  onEditBlur(row: any, column: TableColumn): void {
    if (this.isEditingCell(row, column)) {
      this.editingCell = null;
    }
  }

  onEditKeydown(column: TableColumn, event: KeyboardEvent): void {
    if (column.type !== 'number' || column.maxDecimals == null) return;
    if (event.key.length > 1 || event.ctrlKey || event.metaKey || event.altKey) return;

    const input = event.target as HTMLInputElement;
    const start = input.selectionStart ?? input.value.length;
    const end = input.selectionEnd ?? start;
    const prospective = input.value.slice(0, start) + event.key + input.value.slice(end);
    if (!this.decimalTextPattern(column.maxDecimals).test(prospective)) {
      event.preventDefault();
    }
  }

  getEditValue(row: any, column: TableColumn): any {
    if (this.isEditingCell(row, column)) {
      return this.editingCell!.text;
    }
    const value = row[column.key];
    if (column.editFormatter) {
      return column.editFormatter(value);
    }
    // Normalize null/undefined to '' — text inputs would otherwise render the literal string "undefined".
    return value ?? '';
  }

  onValueChange(row: any, column: TableColumn, event: Event, rowIndex: number): void {
    const input = event.target as HTMLInputElement;
    const value: any = column.type === 'number' ? this.parseNumberInput(input, column) : input.value;

    if (this.isEditingCell(row, column)) {
      this.editingCell!.text = input.value;
    }

    row[column.key] = value;
    this.cellValueChange.emit({ row, column, value, rowIndex });
  }

  private parseNumberInput(input: HTMLInputElement, column: TableColumn): number | null {
    if (column.maxDecimals == null) {
      return input.value ? Number.parseFloat(input.value) : null;
    }

    if (input.value && !this.decimalTextPattern(column.maxDecimals).test(input.value)) {
      input.value = this.sanitizeDecimalText(input.value, column.maxDecimals);
    }

    const parsed = Number.parseFloat(input.value.replace(',', '.'));
    return input.value && !Number.isNaN(parsed) ? parsed : null;
  }

  private decimalTextPattern(maxDecimals: number): RegExp {
    return maxDecimals === 0
      ? /^-?\d*$/
      : new RegExp(String.raw`^-?\d*([.,]\d{0,${maxDecimals}})?$`);
  }

  private sanitizeDecimalText(text: string, maxDecimals: number): string {
    let cleaned = text.replace(/[^0-9.,-]/g, '');
    cleaned = cleaned.startsWith('-') ? '-' + cleaned.slice(1).replace(/-/g, '') : cleaned.replace(/-/g, '');
    const sepIndex = cleaned.search(/[.,]/);
    if (sepIndex < 0) return cleaned;
    const whole = cleaned.slice(0, sepIndex);
    if (maxDecimals === 0) return whole;
    const separator = cleaned[sepIndex];
    const fraction = cleaned.slice(sepIndex + 1).replace(/[.,]/g, '').slice(0, maxDecimals);
    return `${whole}${separator}${fraction}`;
  }

  onCurrencyValueChange(row: any, column: TableColumn, value: number | null, rowIndex: number): void {
    row[column.key] = value;
    this.cellValueChange.emit({ row, column, value, rowIndex });
  }

  onRowClick(row: any, rowIndex: number): void {
    this.rowClick.emit({ row, rowIndex });
  }

  getRowClasses(row: any, rowIndex: number, displayIndex: number = rowIndex): string[] {
    const classes: string[] = [];

    if (this.hasTotalRow && rowIndex === this.totalRowIndex) {
      classes.push('total-row');
    }

    if (this.alternateRowColors && displayIndex % 2 === 1) {
      classes.push('alternate-row');
    }
    
    for (const config of this.rowClasses) {
      if (config.condition(row, rowIndex)) {
        classes.push(config.className);
      }
    }
    
    if (this.editMode && rowIndex >= this.editableFromIndex) {
      const rowIsEditable = !this.isRowEditable || this.isRowEditable(row, rowIndex);
      if (rowIsEditable) {
        classes.push('edit-mode-row');
      }
    }
    
    return classes;
  }

  isCellEditable(column: TableColumn, row: any, rowIndex: number): boolean {
    if (!this.editMode || column.editable === false) {
      return false;
    }
    if (rowIndex < this.editableFromIndex) {
      return false;
    }
    if (this.hasTotalRow && rowIndex === this.totalRowIndex) {
      return false;
    }
    if (this.isRowEditable && !this.isRowEditable(row, rowIndex)) {
      return false;
    }
    if (column.isCellEditableFn && !column.isCellEditableFn(row, rowIndex)) {
      return false;
    }
    return true;
  }

  getColumnStyle(column: TableColumn): { [key: string]: string } {
    const style: { [key: string]: string } = {};

    if (column.width) {
      style['width'] = column.width;
    }

    const contentAlign = column.contentAlign ?? column.align;
    if (contentAlign) {
      style['text-align'] = contentAlign;
    }

    return style;
  }

  getHeaderColumnStyle(column: TableColumn): { [key: string]: string } {
    const style: { [key: string]: string } = {};

    if (column.width) {
      style['width'] = column.width;
    }

    const headerAlign = column.headerAlign ?? column.align;
    if (headerAlign) {
      style['text-align'] = headerAlign;
    }

    return style;
  }

  getHeaderClass(column: TableColumn): string {
    const headerAlign = column.headerAlign ?? column.align;
    return headerAlign ? `align-${headerAlign}` : 'align-left';
  }

  getInputType(column: TableColumn): string {
    if (column.type === 'number' && column.maxDecimals != null) {
      // Text input (with keydown filtering) — number inputs don't expose the caret position,
      // which we need to block extra decimals without resetting the cursor.
      return 'text';
    }
    if (column.type === 'number' || column.type === 'currency') {
      return 'number';
    }
    return 'text';
  }

  private formatCurrency(value: number): string {
    return value.toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  }

  trackByRow(index: number, row: any): any {
    return row && row[this.trackByKey] !== undefined ? row[this.trackByKey] : index;
  }

  getCellTooltip(row: any, column: TableColumn): string | null {
    if (column.tooltip) {
      return column.tooltip(row[column.key], row);
    }
    return null;
  }

  getCellClasses(row: any, column: TableColumn, rowIndex: number): string[] {
    const classes: string[] = [];
    
    if (column.cellClass) {
      const result = column.cellClass(row[column.key], row, rowIndex);
      if (result) {
        if (Array.isArray(result)) {
          classes.push(...result);
        } else {
          classes.push(result);
        }
      }
    }
    
    return classes;
  }
}

